// Receptpagina: stappen afvinken, porties omrekenen, programma bij de stap, favoriet bewaren, printen, delen en de
// koopbalk op mobiel. Favorieten staan alleen in deze browser (localStorage), er gaat niets naar de winkel.

import { readFavourites, writeFavourites } from './recipe-favourites.js';

class RecipePage extends HTMLElement {
  connectedCallback() {
    const steps = this.querySelector('[data-steps]');
    if (steps) {
      steps.querySelectorAll('li').forEach((item) => this.makeCheckable(item));
      this.querySelector('[data-steps-hint]')?.removeAttribute('hidden');
      this.addProgrammeChip(steps);
    }

    this.setupScale();
    this.setupFavourite();
    this.setupBuyBar();

    const print = this.querySelector('[data-print]');
    if (print) {
      print.hidden = false;
      print.addEventListener('click', () => window.print());
    }

    const share = this.querySelector('[data-share]');
    if (share && typeof navigator.share === 'function') {
      share.hidden = false;
      share.addEventListener('click', () => {
        navigator.share({ title: document.title, url: window.location.href }).catch(() => {});
      });
    }

    const actions = this.querySelector('[data-actions]');
    if (actions) actions.hidden = !actions.querySelector('button:not([hidden])');
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  // Een stap wordt een label met een checkbox; de hele kaart is klikbaar.
  makeCheckable(item) {
    if (item.parentElement.closest('li') || item.querySelector('.recipe-step')) return;
    const label = document.createElement('label');
    label.className = 'recipe-step';
    const box = document.createElement('input');
    box.type = 'checkbox';
    box.className = 'recipe-step__box visually-hidden';
    box.addEventListener('change', () => item.classList.toggle('is-done', box.checked));
    label.append(box, ...item.childNodes);
    item.append(label);
  }

  // Het programmalabel komt onder de eerste stap die het programma noemt (hoofdletters maken niet uit).
  addProgrammeChip(steps) {
    const template = this.querySelector('template[data-programme-chip]');
    const programme = template?.dataset.programme?.trim().toLowerCase();
    if (!template || !programme) return;
    const step = [...steps.querySelectorAll('li')].find((item) => item.textContent.toLowerCase().includes(programme));
    if (!step) return;
    (step.querySelector('.recipe-step') || step).append(template.content.cloneNode(true));
  }

  // Porties: 1 tot 10 keer het recept. Alleen regels met een getal rekenen mee.
  setupScale() {
    const scale = this.querySelector('[data-scale]');
    const list = this.querySelector('.recipe-ing');
    if (!scale || !list || !list.querySelector('[data-amount]')) return;
    const output = scale.querySelector('[data-scale-value]');
    const decimal = list.dataset.decimal || '.';
    let factor = 1;
    const render = () => {
      output.textContent = `${factor}×`;
      list.querySelectorAll('[data-amount]').forEach((item) => {
        const qty = item.querySelector('[data-qty]');
        const amount = Number.parseFloat(item.dataset.amount);
        if (!qty || Number.isNaN(amount)) return;
        const value = Math.round(amount * factor * 10) / 10;
        const text = Number.isInteger(value) ? String(value) : String(value).replace('.', decimal);
        qty.textContent = item.dataset.unit ? `${text} ${item.dataset.unit}` : text;
      });
      scale.querySelector('[data-scale-step="-1"]').disabled = factor <= 1;
      scale.querySelector('[data-scale-step="1"]').disabled = factor >= 10;
    };
    scale.querySelectorAll('[data-scale-step]').forEach((button) => {
      button.addEventListener('click', () => {
        factor = Math.min(10, Math.max(1, factor + Number(button.dataset.scaleStep)));
        render();
      });
    });
    scale.hidden = false;
    render();
  }

  setupFavourite() {
    const button = this.querySelector('[data-favourite]');
    if (!button) return;
    const handle = button.dataset.favourite;
    const label = button.querySelector('[data-favourite-label]');
    const render = () => {
      const on = readFavourites().includes(handle);
      button.setAttribute('aria-pressed', String(on));
      if (label) label.textContent = on ? label.dataset.on : label.dataset.off;
    };
    button.addEventListener('click', () => {
      const list = readFavourites();
      const next = list.includes(handle) ? list.filter((item) => item !== handle) : [...list, handle];
      if (writeFavourites(next)) render();
    });
    // Zonder localStorage (privévenster, geblokkeerd) blijft de knop verborgen.
    if (writeFavourites(readFavourites())) {
      button.hidden = false;
      render();
    }
  }

  // De koopbalk verschijnt zodra het blok Gemaakt met voorbij is, en verdwijnt zolang de kaart van het apparaat bij
  // Wat heb je nodig in beeld is: nooit twee koopknoppen tegelijk.
  setupBuyBar() {
    const bar = this.querySelector('[data-buy-bar]');
    const made = this.querySelector('[data-made-with]');
    if (!bar || !made || !('IntersectionObserver' in window)) return;
    const card = this.querySelector('.recipe-need--device');
    const seen = new Map();
    bar.hidden = false;
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => seen.set(entry.target, entry));
      const madeEntry = seen.get(made);
      const passed = madeEntry && !madeEntry.isIntersecting && madeEntry.boundingClientRect.top < 0;
      const cardVisible = card && seen.get(card)?.isIntersecting;
      bar.classList.toggle('is-visible', Boolean(passed && !cardVisible));
    });
    this.observer.observe(made);
    if (card) this.observer.observe(card);
  }
}

customElements.define('recipe-page', RecipePage);
